class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.hoverMedia = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
    this.setHeaderCartIconAccessibility();
    this.addEventListener('pointerover', (event) => {
      if (event.target.closest('.drawer__inner')) this.clearPeekTimers();
    });
    this.addEventListener('pointerout', (event) => {
      const panel = this.querySelector('.drawer__inner');
      if (panel?.contains(event.target) && !panel.contains(event.relatedTarget)) this.schedulePeekClose();
    });
    this.addEventListener('focusout', () => this.schedulePeekClose());
    document.addEventListener('pointerdown', (event) => {
      if (!this.contains(event.target) && !event.target.closest('#cart-icon-bubble')) this.closePeek();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closePeek();
    });
    window.addEventListener('scroll', () => this.closePeek(), { passive: true });
    window.addEventListener('resize', () => this.closePeek());
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.setAttribute('aria-controls', 'CartDrawer');
    cartLink.setAttribute('aria-expanded', 'false');
    cartLink.addEventListener('pointerenter', () => {
      this.clearPeekTimers();
      if (this.hoverMedia.matches && !this.classList.contains('active')) {
        this.peekOpenTimer = window.setTimeout(() => this.openPeek(cartLink), 140);
      }
    });
    cartLink.addEventListener('pointerleave', () => {
      window.clearTimeout(this.peekOpenTimer);
      this.schedulePeekClose();
    });
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  clearPeekTimers() {
    window.clearTimeout(this.peekOpenTimer);
    window.clearTimeout(this.peekCloseTimer);
  }

  openPeek(cartLink) {
    if (!this.hoverMedia.matches || this.classList.contains('active')) return;
    const box = cartLink.getBoundingClientRect();
    if (box.bottom < 0 || box.top > window.innerHeight) return;
    this.style.setProperty('--cart-peek-top', Math.round(box.bottom + 12) + 'px');
    this.style.setProperty('--cart-peek-right', Math.max(16, Math.round(window.innerWidth - box.right)) + 'px');
    this.querySelector('.drawer__inner').setAttribute('aria-modal', 'false');
    cartLink.setAttribute('aria-expanded', 'true');
    this.classList.add('hover-peek', 'active');
  }

  schedulePeekClose() {
    window.clearTimeout(this.peekCloseTimer);
    this.peekCloseTimer = window.setTimeout(() => {
      if (!this.querySelector('.drawer__inner')?.matches(':hover, :focus-within')) this.closePeek();
    }, 280);
  }

  closePeek() {
    this.clearPeekTimers();
    if (!this.classList.contains('hover-peek')) return;
    this.classList.remove('active', 'hover-peek');
    this.querySelector('.drawer__inner')?.setAttribute('aria-modal', 'true');
    document.querySelector('#cart-icon-bubble')?.setAttribute('aria-expanded', 'false');
  }

  open(triggeredBy) {
    this.clearPeekTimers();
    this.classList.remove('hover-peek');
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.querySelector('.drawer__inner').setAttribute('aria-modal', 'true');
    document.querySelector('#cart-icon-bubble')?.setAttribute('aria-expanded', 'true');
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    this.classList.add('animate', 'active');
    document.body.classList.add('overflow-hidden');
    window.requestAnimationFrame(() => {
      if (this.classList.contains('active') && !this.classList.contains('hover-peek')) {
        const panel = this.querySelector('.drawer__inner');
        trapFocus(panel, panel);
      }
    });
  }

  close() {
    if (this.classList.contains('hover-peek')) {
      this.closePeek();
      return;
    }
    this.classList.remove('active');
    document.querySelector('#cart-icon-bubble')?.setAttribute('aria-expanded', 'false');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

